import { useCallback, useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AuthContext } from "./Contexts/AuthContext";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaChartBar, FaEye, FaEyeSlash, FaShieldAlt, FaSync } from "react-icons/fa";
import { FaCow } from "react-icons/fa6";
import { MdMobileFriendly } from "react-icons/md";
import { api } from "./api";
import "./dash.css";
import SocialLogin from "./SocialLogin/SocialLogin";
import { useTranslation } from "react-i18next";
import logo from "./screenshots/Milkfarm.jpeg";
export default function Dash() {
  const { user, login } = useContext(AuthContext);
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: "",
    password: "",
    captcha: "",
    captcha_key: "",
  });

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [captchaUrl, setCaptchaUrl] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const loadCaptcha = useCallback(async () => {
    try {
      setCaptchaLoading(true);
      const { data } = await axios.get(api + "captcha");
      const captchaImage = data?.captcha || "";
      const key = data?.key || "";

      setCaptchaUrl(captchaImage);
      setForm((prev) => ({ ...prev, captcha_key: key, captcha: "" }));
    } catch (captchaError) {
      console.error("Captcha load error:", captchaError);
      setError(t("login.captchaLoadError"));
    } finally {
      setCaptchaLoading(false);
    }
  }, [t]);

  useEffect(() => {
    const verified = searchParams.get("verified");
    if (verified === "success") {
      setSuccess(true);
    }
  }, [searchParams]);

  useEffect(() => {
    loadCaptcha();
  }, [loadCaptcha]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token && user) {
      navigate("/" + user.role);
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!form.captcha.trim()) {
      setError(t("login.captchaError"));
      return;
    }

    if (!form.captcha_key) {
      setError(t("login.captchaLoadError"));
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(api + "login", form);
      login(res.data.user);
      localStorage.setItem("token", res.data.token);
      if (res.data.user.role === "superadmin") {
        navigate("/superadmin");
      } else if (res.data.user.role === "admin") {
        navigate("/admin");
      } else {
        navigate("/eleveur");
      }
    } catch (err) {
      setForm((prev) => ({ ...prev, captcha: "" }));
      loadCaptcha();

      const apiErrors = err.response?.data?.errors;
      const firstError = String(Object.values(apiErrors || {}).flat()[0] || "").toLowerCase();
      const message = String(err.response?.data?.message || "");
      const loweredMessage = message.toLowerCase();

      if (firstError.includes("captcha") || loweredMessage.includes("captcha")) {
        setError(t("login.captchaInvalid"));
      } else {
        setError(message || t("login.connectionError"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-left">
        <div className="login-content">
          <div className="logo-section">
            <div className="logo-icon">
              <img src={logo} alt="MilkFarm Logo" className="logo-image" />
            </div>
            <h1 className="logo-text">{t("login.brand")}</h1>
          </div>

          <div className="welcome-text">
            <h2>{t("login.welcomeTitle")}</h2>
            <p>{t("login.welcomeSubtitle")}</p>
          </div>

          <div className="mobile-summary-card">
            <div className="mobile-summary-badge">{t("login.heroBadge")}</div>
            <strong>{t("login.mobileTitle")}</strong>
            <p>{t("login.mobileDesc")}</p>
          </div>

          {success && (
            <div className="success-message">
              {t("login.emailVerified")}
            </div>
          )}

          {error && (
            <div className="error-message">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              {error}
            </div>
          )}

          <SocialLogin />

          <form onSubmit={handleSubmit} className="login-form">
            <div className="input-group">
              <label htmlFor="email">{t("login.email")}</label>
              <div className="input-wrapper">
                <svg className="input-icon" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
                <input
                  id="email"
                  className="input-field"
                  type="email"
                  name="email"
                  placeholder={t("login.emailPlaceholder")}
                  value={form.email}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="input-group">
              <label htmlFor="password">{t("login.password")}</label>
              <div className="input-wrapper">
                <svg className="input-icon" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                <input
                  id="password"
                  className="input-field"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder={t("login.passwordPlaceholder")}
                  value={form.password}
                  onChange={handleChange}
                  required
                />
                <button
                  type="button"
                  className="input-toggle"
                  onClick={() => setShowPassword((value) => !value)}
                  aria-label={showPassword ? t("profile.hidePassword") : t("profile.showPassword")}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            <div className="captcha-group">
              <label htmlFor="captcha">{t("login.captchaField")}</label>
              <div className="captcha-row">
                <div className="captcha-visual" aria-live="polite">
                  {captchaUrl ? (
                    <img src={captchaUrl} alt={t("login.captcha.alt")} />
                  ) : (
                    <span className="captcha-placeholder">{t("login.captcha.loading")}</span>
                  )}
                </div>
                <button
                  type="button"
                  className="captcha-refresh"
                  onClick={loadCaptcha}
                  disabled={captchaLoading}
                  aria-label={t("login.captcha.refresh")}
                >
                  <FaSync />
                </button>
              </div>
              <input
                id="captcha"
                name="captcha"
                className="input-field captcha-input"
                type="text"
                value={form.captcha}
                onChange={handleChange}
                placeholder={t("login.captchaPlaceholder")}
                autoComplete="off"
                required
              />
            </div>

            <button
              className="submit-button"
              type="submit"
              disabled={loading}
            >
              {loading ? (
                <>
                  <svg className="spinner" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeDasharray="50.265" strokeDashoffset="25" />
                  </svg>
                  {t("login.loggingIn")}
                </>
              ) : (
                <>
                  {t("login.loginButton")}
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </>
              )}
            </button>

            <div className="form-footer">
              <p>
                {t("login.noAccount")} {" "}
                <a href="/signup" className="link-primary">{t("login.signup")}</a>
              </p>
            </div>
          </form>
        </div>
      </div>

      <div className="login-right">
        <div className="hero-content">
          <div className="hero-badge">
            <span className="badge-dot"></span>
            {t("login.heroBadge")}
          </div>

          <h1 className="hero-title">
            {t("login.heroTitle1")}
            <span className="gradient-text"> {t("login.heroTitle2")}</span>
          </h1>

          <p className="hero-description">
            {t("login.heroDescription")}
          </p>

          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon"><FaChartBar /></div>
              <h3>{t("login.statsTitle")}</h3>
              <p>{t("login.statsDesc")}</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon"><MdMobileFriendly /></div>
              <h3>{t("login.mobileTitle")}</h3>
              <p>{t("login.mobileDesc")}</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon"><FaShieldAlt /></div>
              <h3>{t("login.securityTitle") || "Sécurisé"}</h3>
              <p>{t("login.securityDesc") || "Connexion protégée et données chiffrées"}</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon"><FaCow /></div>
              <h3>{t("login.herdTitle") || "Gestion complète"}</h3>
              <p>{t("login.herdDesc") || "Tout votre troupeau en un seul endroit"}</p>
            </div>
          </div>
        </div>

        <div className="bg-decoration decoration-1"></div>
        <div className="bg-decoration decoration-2"></div>
        <div className="bg-decoration decoration-3"></div>
        <div className="bg-grid"></div>
      </div>
    </div>
  );
}
